import { progressText } from './progress'
/** The board keeps current work prominent and finished jobs compact. */
import { Link } from 'react-router-dom'
import { Icon } from '@/components/Icon'
import { RobotAvatar } from './RobotAvatar'
import { fmtDateTime } from '@/lib/format'
import { isLive, isPending, jobState } from './job'
import type { BoardJob, Job } from './types'
import { messageLink, type AuthorMessage } from './board'

export function AgentStatus({ status, job }: { status: string; job?: Job }) {
  const state = jobState(status, job)
  const label = status === 'queued' ? 'Queued' : state.label
  return <span className={`agent-status ${state.tone}`}>{label}</span>
}

/** A researcher reads a template and proposes workflows; everything else builds one. */
export const isResearcher = (job: Pick<Job, 'job_type'>) => job.job_type === 'workflow-research'

/** A quiet identity mark; motion indicates a running build, never progress. */
export function AgentIdentity({ identity, name, program, researcher = false, running = false }: { identity: string; name: string; program?: string; researcher?: boolean; running?: boolean }) {
  return <span className={`agent-identity${researcher ? ' is-researcher' : ''}${running ? ' is-running' : ''}`}>
    <span className="agent-identity-mark" aria-hidden="true"><RobotAvatar identity={identity} /></span>
    <span className="agent-identity-text"><strong>{name}
      {researcher && <span className="agent-role-mark" title="Researcher: proposes workflows from a template"><Icon name="search" size={12} /></span>}
    </strong>{program && <span>{program}</span>}</span>
  </span>
}

function elapsed(createdAt: string, now: number) {
  const minutes = Math.max(0, Math.floor((now - new Date(createdAt).getTime()) / 60000))
  return minutes < 1 ? 'Just started' : minutes < 60 ? `${minutes}m elapsed` : `${Math.floor(minutes / 60)}h ${minutes % 60}m elapsed`
}

function RecentMessages({ messages }: { messages?: AuthorMessage[] | null }) {
  return <div className="agent-recent-messages">
    <span className="agent-recent-label">Recent messages</span>
    {messages === undefined ? <span className="muted">Loading…</span>
      : messages === null ? <span className="muted">Messages unavailable</span>
      : messages.length ? messages.map(({ thread_id, message }) =>
        <Link key={message.id} to={messageLink(thread_id, message.kind === 'reply' ? message.id : undefined)} title={message.content}>
          <Icon name="chat" size={14} /><span>{message.content}</span><Icon name="arrow" size={13} />
        </Link>) : <span className="muted">No messages yet</span>}
  </div>
}

export function ActiveAgent({ job, now, messages }: { job: BoardJob; now: number; messages?: AuthorMessage[] | null }) {
  return <article className={`experiment-card agent-build-card${isResearcher(job) ? ' is-researcher' : ''}${job.status === 'running' && job.phase !== 'account_admission' ? ' is-running' : ''}`}>
    <Link className="agent-build-main" to={`/eng/agents/${job.task_id}/${job.job_id}`}>
    <div className="agent-build-top"><AgentIdentity identity={job.task_id} name={job.agent_name || 'Verifier build'} program={job.program} researcher={isResearcher(job)} running={job.status === 'running' && job.phase !== 'account_admission'} /><AgentStatus status={job.status} job={job} /></div>
    <h3>{job.title || job.task_id}</h3>
    <p className="agent-build-latest">{job.status === 'capacity_wait'
      ? `${jobState(job.status, job).label}. Next check ${fmtDateTime(new Date((job.capacity_wait?.retry_at ?? 0) * 1000).toISOString())}.`
      : progressText(job)}</p>
    <div className="experiment-card-bottom">
      <span title={`${job.total} of ${job.limits.total} allowed rollout attempts`}>{job.total} {job.total === 1 ? 'attempt' : 'attempts'}{job.pending > 0 && <> · {job.pending} in flight</>}</span>
      <span title={fmtDateTime(job.created_at)}>{job.status === 'queued' ? elapsed(job.created_at, now).replace('elapsed', 'waiting').replace('Just started', 'Just queued') : elapsed(job.created_at, now)}</span>
    </div>
    </Link>
    <RecentMessages messages={messages} />
  </article>
}

export function AgentHistory({ jobs, previousIds, messagesByTask }: { jobs: BoardJob[]; previousIds?: Set<string>; messagesByTask?: Record<string, AuthorMessage[] | null> }) {
  return <div className="agent-history">{jobs.map(job => <article className="agent-history-entry" key={job.job_id}>
    <Link className="agent-history-row" to={`/eng/agents/${job.task_id}/${job.job_id}`}>
    <div className="agent-history-task">
      <strong>{job.title || job.task_id}</strong>
      <div className="agent-history-meta"><span className="agent-history-name"><RobotAvatar identity={job.task_id} size={24} />{job.agent_name || job.job_id}
        {isResearcher(job) && <span className="agent-role-mark" title="Researcher: proposes workflows from a template"><Icon name="search" size={12} /></span>}</span><span>{job.program}</span><span>{job.total} attempts</span></div>
      {job.blocker && <p className="agent-history-blocker">{job.blocker}</p>}
    </div>
    <div className="agent-history-outcome">{previousIds?.has(job.job_id) ? <span className="agent-status">Previous attempt</span> : <AgentStatus status={job.status} job={job} />}<time dateTime={job.finished_at || job.created_at}>{fmtDateTime(job.finished_at || job.created_at)}</time></div>
    <Icon name="arrow" size={16} />
    </Link>
    <RecentMessages messages={messagesByTask?.[job.task_id]} />
  </article>)}</div>
}

export const needsAttention = (job: BoardJob) => !isLive(job.status) && !isPending(job.status) && job.status !== 'succeeded' && job.status !== 'cancelled'
